/**
 * User-facing English defaults for `@ardoq/table` components.
 *
 * Each component accepts an optional `labels?: Partial<XxxLabels>` prop. When
 * a consumer threads translated strings, the package renders them; otherwise
 * the English defaults below are used. The package itself stays
 * translation-free (Pattern A).
 */

export type NameCellLabels = {
  /** Tooltip on the copy-name icon button. */
  copyName: string;
};

export const DEFAULT_NAME_CELL_LABELS: NameCellLabels = {
  copyName: 'Copy name',
};

export type TransformationCellLabels = {
  /** Aria label for the recalculation icon button. */
  triggerRecalculation: string;
};

export const DEFAULT_TRANSFORMATION_CELL_LABELS: TransformationCellLabels = {
  triggerRecalculation: 'Trigger recalculation',
};

export type ActionsCellLabels = {
  /** Aria label for the kebab menu trigger. */
  actions: string;
};

export const DEFAULT_ACTIONS_CELL_LABELS: ActionsCellLabels = {
  actions: 'Actions',
};

export type GroupCellLabels = {
  /** Aria label for the row drag handle. */
  dragHandle: string;
  /** Label for the chevron when the group is currently expanded. */
  collapseGroup: string;
  /** Label for the chevron when the group is currently collapsed. */
  expandGroup: string;
  /** Aria label for the inline rename input. */
  inlineTextEditor: string;
};

export const DEFAULT_GROUP_CELL_LABELS: GroupCellLabels = {
  dragHandle: 'Drag to reorder',
  collapseGroup: 'Collapse group',
  expandGroup: 'Expand group',
  inlineTextEditor: 'Inline text editor',
};

export type FloatingActionBarLabels = {
  /** Aria label for the toolbar wrapper. */
  actionsForSelectedItems: string;
  /** Aria label for the overflow menu trigger. */
  moreActions: string;
  /** Label for the cancel/clear-selection button. */
  cancel: string;
  /**
   * Renders the "{count} {entity} selected" string.
   * Receives the formatted count and entity name; default uses English
   * "{count} {entity} selected" word order.
   */
  formatSelectedCount: (params: {
    selectedCount: number;
    entityName: string;
  }) => string;
};

const defaultFormatSelectedCount = ({
  selectedCount,
  entityName,
}: {
  selectedCount: number;
  entityName: string;
}): string => `${selectedCount} ${entityName} selected`;

export const DEFAULT_FLOATING_ACTION_BAR_LABELS: FloatingActionBarLabels = {
  actionsForSelectedItems: 'Actions for selected items',
  moreActions: 'More actions',
  cancel: 'Cancel',
  formatSelectedCount: defaultFormatSelectedCount,
};

export type TableEmptyStateLabels = {
  /** Title of the default no-results empty state. */
  noResultsTitle: string;
  /** Description of the default no-results empty state. */
  noResultsDescription: string;
};

export const DEFAULT_TABLE_EMPTY_STATE_LABELS: TableEmptyStateLabels = {
  noResultsTitle: 'No results found',
  noResultsDescription:
    'You may have applied filters, or no data has been added yet.',
};

export type SelectionColumnLabels = {
  /**
   * Visually hidden accessible name for the row-selection column header. Without
   * it the column header is announced as an unnamed `columnheader`, which is all
   * a screen reader gets when `hideHeaderCheckbox` is set.
   */
  header: string;
};

export const DEFAULT_SELECTION_COLUMN_LABELS: SelectionColumnLabels = {
  header: 'Select',
};

/**
 * AG Grid built-in `localeText` keys surfaced by `@ardoq/table`.
 *
 * The package itself stays translation-free (Pattern A) — consumers supply a
 * `tableLocaleTextLabels` map built from their i18n locale, and `Table` merges
 * it into AG Grid's `gridOptions.localeText`. Keep this surface limited to the
 * AG Grid strings that actually appear in the menu / overlay configurations
 * produced by `ControlledAGGrid` (column header menu, row-group drop zone,
 * loading + empty overlays, autosize defaults consumed by `menuItems.ts`).
 *
 * Key names must match AG Grid's `localeText` keys verbatim. Adding a key here
 * makes it eligible for translation; AG Grid falls back to its English default
 * if a consumer doesn't provide a value.
 */
export type TableLocaleTextLabels = {
  // Sort menu
  sortAscending: string;
  sortDescending: string;
  sortUnSort: string;

  // Pin sub-menu
  pinColumn: string;
  pinLeft: string;
  pinRight: string;
  noPin: string;

  // Column visibility
  /** Menu item that opens the column chooser. */
  columnChooser: string;
  /** Column-chooser dialog title (distinct from the columnChooser menu item). */
  chooseColumns?: string;
  resetColumns: string;

  // Row-group menu (auto group column)
  expandAll: string;
  contractAll: string;

  // Autosize defaults consumed by menuItems.ts via the same keys
  autosizeThisColumn: string;
  autosizeAllColumns: string;

  // Row-grouping drop zone placeholder
  rowGroupColumnsEmptyMessage: string;

  // Overlays
  noRowsToShow: string;
  loadingOoo: string;

  // Row selection accessibility
  /** Accessible name of a row checkbox, when the table supplies no row label. */
  ariaRowToggleSelection: string;
  ariaRowSelectionDisabled: string;
  /** Accessible name of the select-all checkbox in the column header. */
  ariaHeaderSelection: string;
  ariaRowSelectAll: string;
};

export const DEFAULT_TABLE_LOCALE_TEXT_LABELS: TableLocaleTextLabels = {
  sortAscending: 'Sort Ascending',
  sortDescending: 'Sort Descending',
  sortUnSort: 'Clear Sort',
  pinColumn: 'Pin Column',
  pinLeft: 'Pin Left',
  pinRight: 'Pin Right',
  noPin: 'No Pin',
  columnChooser: 'Choose Columns',
  chooseColumns: 'Choose Columns',
  resetColumns: 'Reset Columns',
  expandAll: 'Expand All Row Groups',
  contractAll: 'Close All Row Groups',
  autosizeThisColumn: 'Autosize This Column',
  autosizeAllColumns: 'Autosize All Columns',
  rowGroupColumnsEmptyMessage: 'Drag columns here to set row groups',
  noRowsToShow: 'No Rows To Show',
  loadingOoo: 'Loading…',
  ariaRowToggleSelection: 'Select row',
  ariaRowSelectionDisabled: 'Selection is not available for this row',
  ariaHeaderSelection: 'Select all rows',
  ariaRowSelectAll: 'Toggle selection of all rows',
};
